import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackDisabledSelectAll } from '@air/analytics';
import { type Box, type useSelectionContainer } from '@air/react-drag-to-select';
import {
  resetSelectedItemsAction,
  selectClickDragHighlightedItemsAction,
  selectedItemsCountSelector,
  startMouseSelectionAction,
} from '@air/redux-selected-items';

import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useConfirmMouseSelection } from '~/hooks/useConfirmMouseSelection';
import { type MarqueeSelectableItem, useMarqueeSelection } from '~/hooks/useMarqueeSelection';
import { type UseMouseScrollingParams, useMouseScrolling } from '~/hooks/useMouseScrolling';
import { useMouseSelectionContext } from '~/providers/MouseSelectionProvider';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import {
  isAnyMenuOverlayOpen,
  isMenuOrButtonClicked,
  isScrollbarClick,
  isSelectionDisabled,
} from '~/utils/mouseSelectionGuards';
import { useAirStore } from '~/utils/ReduxUtils';

// The marquee's drag listeners live on this element, and the disable-hover class MUST be toggled
// on this same element — never an ancestor of it. `.disable-hover *` removes pointer-events from
// every descendant of the class carrier, so mousemoves re-target to the carrier itself; carried
// any higher (body above #__next), moves stop passing through the marquee's listener element and
// the selection box freezes mid-drag. #__next (not body) is the scope because modals, menus, and
// toasts portal directly onto <body>, OUTSIDE #__next — listening on body would pull drags inside
// those portals into drag-select (e.g. drawing an annotation in the asset modal). body is only
// the fallback for app-router pages, which never render #__next. useMouseScrolling's auto-scroll
// replay dispatches to the same element.
const getMarqueeEventsElement = () => document.getElementById('__next') ?? document.body;

export type MouseSelectableItem = {
  box: Box;
  selectableItem: SelectableGalleryItem | null;
};

export type UseSelectedItemsMouseSelectionParams = {
  /** The list container — the coordinate frame the marquee (drag-to-select rectangle) is computed
   * against. */
  containerRef: RefObject<HTMLElement>;
  /** Positions of the list's elements, hit-tested against the marquee rectangle. */
  rowsPositions: MouseSelectableItem[];
  /** The scroll viewport — auto-scrolled when a drag nears its edges, and read by the deselect
   * listener's scrollbar-click heuristic. */
  scrollElementRef: UseMouseScrollingParams['scrollElementRef'];
  minSelectionTopPosition?: number;
};

/**
 * This hook enables mouse selection in lists like gallery view, boards grid or table view.
 * It is NOT a generic primitive: selection commits into the app's central Redux selected-items
 * store (plus the select-all Zustand store and its analytics), and the events surface is the app
 * page (`#__next`). Private and public pages alike use it because they share that store; surfaces
 * with their own selection state (like the ItemPicker) cannot. The reusable, policy-free
 * primitives it composes live in `useMarqueeSelection` and `useMouseScrolling`.
 *
 * A third element — beyond the params' `containerRef`/`scrollElementRef` (documented on
 * UseSelectedItemsMouseSelectionParams) — is the SELECTION AREA: the surface where a plain click
 * clears the current selection. It comes from the nearest optional MouseSelectionProvider; with
 * no provider, `window` is the designed default (a plain click anywhere clears).
 */
export const useSelectedItemsMouseSelection = ({
  containerRef,
  rowsPositions,
  scrollElementRef,
  minSelectionTopPosition = 0,
}: UseSelectedItemsMouseSelectionParams): ReturnType<typeof useSelectionContainer> => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const { confirmMouseSelection } = useConfirmMouseSelection();
  const mouseSelectionContext = useMouseSelectionContext();

  useMouseScrolling({ scrollElementRef });
  // Whether cmd/ctrl was held on the mousedown that began the current drag-selection.
  // Captured from the event (below) so the drag library's event-less onSelectionStart
  // can still know — no global keydown tracker needed.
  const holdingCtrlRef = useRef<boolean>(false);
  // Whether a menu overlay was open at the moment the current press began. Captured at pointerdown
  // because Radix unmounts the menu synchronously on pointerdown-outside — by mousedown it is gone.
  const menuWasOpenOnPointerDownRef = useRef<boolean>(false);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  // Clears the current selection on a plain click inside the selection area.
  const deselectOnMouseDown = useCallback(
    (e: MouseEvent) => {
      // Record cmd/ctrl from this press so onSelectionStart (which gets no event) can read it.
      holdingCtrlRef.current = e.metaKey || e.ctrlKey;
      const { target } = e;

      if (!(target instanceof Element)) {
        return;
      }

      if (
        e.button === 0 &&
        // A click with a menu or a selection-preserving overlay open should only dismiss it, not
        // also cancel the selection. Read from the pointerdown capture — the overlay is already
        // unmounted by mousedown.
        !menuWasOpenOnPointerDownRef.current &&
        !isScrollbarClick(target, scrollElementRef.current) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.shiftKey &&
        !isSelectionDisabled(target) &&
        !isMenuOrButtonClicked(target) &&
        selectedItemsCountSelector(store.getState())
      ) {
        if (selectAll) {
          trackDisabledSelectAll({
            totalSelectionCount: selectedItemsCountSelector(store.getState()),
            trigger: 'mouse-selection',
          });
          resetSelectAll();
        }
        dispatch(resetSelectedItemsAction());
      }
    },
    [dispatch, scrollElementRef, store, selectAll, resetSelectAll, trackDisabledSelectAll],
  );

  // Probes for an open menu before Radix can tear it down. Capture phase is required: on the
  // `window` fallback a bubble-phase listener would run after Radix's document-level dismiss.
  const captureMenuStateOnPointerDown = useCallback(() => {
    menuWasOpenOnPointerDownRef.current = isAnyMenuOverlayOpen();
  }, []);

  useEffect(() => {
    // Nearest provider wins — a nested surface (e.g. the collection link editor's edit canvas)
    // binds to ITS selection area even when an ancestor renders its own. No provider is a valid,
    // designed state: `window` — a plain click ANYWHERE clears — is the default for surfaces that
    // declare no bounded area (and the safe superset should a provided ref ever be unassigned).
    const selectionAreaElement = mouseSelectionContext?.selectionArea.current ?? window;

    selectionAreaElement.addEventListener('pointerdown', captureMenuStateOnPointerDown, { capture: true });
    // @ts-expect-error
    selectionAreaElement.addEventListener('mousedown', deselectOnMouseDown);

    return () => {
      selectionAreaElement.removeEventListener('pointerdown', captureMenuStateOnPointerDown, { capture: true });
      selectionAreaElement.removeEventListener(
        'mousedown',
        // @ts-expect-error
        deselectOnMouseDown,
      );
    };
  }, [captureMenuStateOnPointerDown, deselectOnMouseDown, mouseSelectionContext]);

  const onSelectionStart = useCallback(() => {
    // Disable pointer events to prevent hovering elements while dragging.
    getMarqueeEventsElement().classList.add('disable-hover');

    dispatch(startMouseSelectionAction({ holdingCtrl: holdingCtrlRef.current }));
  }, [dispatch]);

  const onSelectionEnd = useCallback(() => {
    // re-enable pointer events
    getMarqueeEventsElement().classList.remove('disable-hover');

    confirmMouseSelection();
  }, [confirmMouseSelection]);

  const getItems = useCallback((): MarqueeSelectableItem[] => {
    const items: MarqueeSelectableItem[] = [];
    for (const row of rowsPositions) {
      if (row.selectableItem) {
        items.push({ box: row.box, id: row.selectableItem.id });
      }
    }
    return items;
  }, [rowsPositions]);

  const onHighlightChange = useCallback(
    (ids: string[]) => {
      dispatch(selectClickDragHighlightedItemsAction({ ids }));
    },
    [dispatch],
  );

  const hasSelectableItems = useMemo(
    () => rowsPositions.length > 0 && !!rowsPositions.find((row) => row.selectableItem),
    [rowsPositions],
  );

  const shouldStartSelecting = useCallback((target: EventTarget | null) => !isSelectionDisabled(target), []);

  return useMarqueeSelection({
    containerRef,
    eventsElement: typeof document !== 'undefined' ? getMarqueeEventsElement() : null,
    getItems,
    isEnabled: hasSelectableItems,
    minSelectionTopPosition,
    onHighlightChange,
    onSelectionEnd,
    onSelectionStart,
    shouldStartSelecting,
  });
};
